/**
 * TAB-001 — Main / Catalog
 *
 * SCREEN_ID: TAB-001
 * Main entry screen with header, promos, search, filter chips, catalog, tab bar
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { analyticsService } from '@/lib/services/analyticsService';
import {
  CatalogHeader,
  PromoBanners,
  SearchBar,
  FilterChips,
  TabBar,
} from '@/components/features';
import CatalogList from '@/components/features/CatalogList';
import PullToRefreshIndicator from '@/components/features/PullToRefreshIndicator';
import CatalogFiltersModal from '@/components/features/CatalogFiltersModal';
import SaveFilterModal from '@/components/features/SaveFilterModal';
import { useCatalogFilters, type CatalogFiltersState } from '@/hooks/useCatalogFilters';
import { useCatalogData, normalizeAssetUrl } from '@/hooks/useCatalogData';
import { useSaveFilterProfile } from '@/hooks/useSaveFilterProfile';
import { useAIFilterProfile } from '@/hooks/useAIFilterProfile';
import { useAuth } from '@/hooks/useAuth';
import { useUnreadCount } from '@/hooks/useUnreadCount';
import { useUserRole } from '@/hooks/useUserRole';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLoadingMonitor } from '@/hooks/useLoadingMonitor';
import Snowfall from '@/components/effects/Snowfall';

const SCREEN_ID = 'TAB-001';
const HEADER_HEIGHT = 56;
const TAB_BAR_HEIGHT = 56;

interface LocationState {
  forYou?: boolean;
  cityId?: string | null;
  cityName?: string | null;
  applyAIProfile?: boolean;
  applySavedSearch?: boolean;
  savedFilters?: CatalogFiltersState;
  isAISearch?: boolean;
}

const MainCatalog = () => {
  const safeNavigate = useSafeNavigate();
  const navigate = useNavigate();
  const location = useLocation();
  const hasEmittedEvents = useRef(false);
  const hasAppliedNavigationState = useRef(false);
  
  // Get state from navigation
  const locationState = location.state as LocationState | null;
  const isForYouMode = locationState?.forYou ?? false;

  const { unreadCount } = useUnreadCount();
  const { isCurator, isModerator, isAdmin } = useUserRole();
  const { user } = useAuth();
  const { saveFilters, isSaving: isSavingFilters } = useSaveFilterProfile();
  const { aiProfile, refresh: refreshAIProfile } = useAIFilterProfile();
  
  const isMobile = useIsMobile();
  
  // AI filter active state (managed here, passed to FilterChips)
  const [isAIFilterActive, setIsAIFilterActive] = useState(false);

  // Filters hook
  const {
    filters,
    activeFiltersCount,
    setSpeciesKey,
    setBreedIds,
    setBreedNames,
    toggleBreedWithName,
    removeBreed,
    setSex,
    setAgeBuckets,
    toggleAgeBucket,
    setSort,
    setGeoPlaceId,
    restoreFromStorage,
    resetFilters,
    // Extended filters (v2)
    setSizes,
    setColors,
    setHealthSpecialNeeds,
  } = useCatalogFilters({ persist: !isForYouMode });

  // Filters modal state
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  
  // Save filter modal state
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

  // Override city and sort for "Для вас" mode
  const selectedCityId = isForYouMode ? (locationState?.cityId ?? null) : filters.geoPlaceId;
  const selectedCityName = isForYouMode ? (locationState?.cityName ?? '') : (filters.geoPlaceName ?? '');
  const effectiveSort = isForYouMode ? 'popularity:desc' as const : filters.sort;
  
  const [searchQuery, setSearchQuery] = useState('');

  // Catalog data hook
  const {
    viewer,
    catalogItems,
    catalogHasMore,
    isLoading,
    isLoadingMore,
    error,
    handleLoadMore,
    handleRetry,
    handleRefresh,
  } = useCatalogData({
    filters,
    selectedCityId,
    searchQuery,
    isForYouMode,
    effectiveSort,
  });

  // Restore filters from storage when switching back from "Для вас" mode
  useEffect(() => {
    if (!isForYouMode) {
      restoreFromStorage();
    }
  }, [isForYouMode, restoreFromStorage]);

  // Handle navigation state: apply AI profile or saved search filters
  useEffect(() => {
    if (hasAppliedNavigationState.current) return;
    
    // Apply AI profile from quiz or "For You" click
    if (locationState?.applyAIProfile && aiProfile) {
      hasAppliedNavigationState.current = true;
      handleApplyAIProfile(aiProfile.filters);
      setIsAIFilterActive(true);
      // Clear state to prevent re-application
      navigate('/catalog', { replace: true, state: {} });
    }
    // Apply saved search filters
    else if (locationState?.applySavedSearch && locationState.savedFilters) {
      hasAppliedNavigationState.current = true;
      const f = locationState.savedFilters;
      
      if (f.speciesKey !== undefined) setSpeciesKey(f.speciesKey);
      if (f.breedIds) setBreedIds(f.breedIds);
      if (f.breedNames) setBreedNames(f.breedNames);
      if (f.sex) setSex(f.sex);
      if (f.ageBuckets) setAgeBuckets(f.ageBuckets);
      if (f.geoPlaceId !== undefined) {
        setGeoPlaceId(f.geoPlaceId, f.geoPlaceName ?? null);
      }
      if (f.sizes) setSizes(f.sizes);
      if (f.colors) setColors(f.colors);
      if (f.healthSpecialNeeds !== undefined) setHealthSpecialNeeds(f.healthSpecialNeeds);
      
      // If it's an AI search, activate AI mode
      if (locationState.isAISearch) {
        setIsAIFilterActive(true);
      }
      
      // Clear state to prevent re-application
      navigate('/catalog', { replace: true, state: {} });
    }
  }, [locationState, aiProfile, navigate, setSpeciesKey, setBreedIds, setBreedNames, setSex, setAgeBuckets, setGeoPlaceId, setSizes, setColors, setHealthSpecialNeeds]);

  // Monitor loading time and show warnings for slow loads
  useLoadingMonitor(isLoading, {
    operationName: 'catalog',
    warningThresholdMs: 3000,
    errorThresholdMs: 8000,
  });

  // Emit analytics on mount
  useEffect(() => {
    if (hasEmittedEvents.current) return;
    hasEmittedEvents.current = true;
    analyticsService.emitTabMainView(SCREEN_ID);
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleCitySelect = (city: { id: string; name: string } | null) => {
    if (city) {
      setGeoPlaceId(city.id, city.name);
    } else {
      setGeoPlaceId(null, null);
    }
  };

  // Apply AI profile filters helper - defined early for use in useEffect
  const handleApplyAIProfile = useCallback((aiFilters: Partial<CatalogFiltersState>) => {
    if (aiFilters.speciesKey !== undefined) setSpeciesKey(aiFilters.speciesKey);
    if (aiFilters.breedIds) setBreedIds(aiFilters.breedIds);
    if (aiFilters.breedNames) setBreedNames(aiFilters.breedNames);
    if (aiFilters.sex) setSex(aiFilters.sex);
    if (aiFilters.ageBuckets) setAgeBuckets(aiFilters.ageBuckets);
    if (aiFilters.geoPlaceId !== undefined) {
      setGeoPlaceId(aiFilters.geoPlaceId ?? null, aiFilters.geoPlaceName ?? null);
    }
    if (aiFilters.sizes) setSizes(aiFilters.sizes);
    if (aiFilters.colors) setColors(aiFilters.colors);
    if (aiFilters.healthSpecialNeeds !== undefined) setHealthSpecialNeeds(aiFilters.healthSpecialNeeds);
  }, [setSpeciesKey, setBreedIds, setBreedNames, setSex, setAgeBuckets, setGeoPlaceId, setSizes, setColors, setHealthSpecialNeeds]);

  const handleOpenFiltersModal = () => {
    setIsFiltersModalOpen(true);
  };

  const handleSaveFilters = () => {
    setIsSaveModalOpen(true);
  };

  // Generate filter name for modal display
  const generateFilterDisplayName = useCallback(() => {
    const parts: string[] = [];
    if (filters.speciesKey === 'dog') parts.push('Собаки');
    else if (filters.speciesKey === 'cat') parts.push('Кошки');
    
    if (Object.keys(filters.breedNames).length > 0) {
      const breedName = Object.values(filters.breedNames)[0];
      if (breedName) parts.push(breedName);
    }
    
    if (filters.geoPlaceName) parts.push(filters.geoPlaceName);
    
    return parts.length > 0 ? parts.join(', ') : 'Мой поиск';
  }, [filters]);

  const handleConfirmSaveFilters = async (notifyEnabled: boolean) => {
    const result = await saveFilters(filters, undefined, notifyEnabled);
    if (result) {
      setIsSaveModalOpen(false);
    }
  };

  // Apply filters from modal
  const handleApplyFilters = (draft: Partial<typeof filters>) => {
    if (draft.speciesKey !== undefined) setSpeciesKey(draft.speciesKey);
    if (draft.breedIds) setBreedIds(draft.breedIds);
    if (draft.sex) setSex(draft.sex);
    if (draft.ageBuckets) setAgeBuckets(draft.ageBuckets);
    if (draft.geoPlaceId !== undefined || draft.geoPlaceName !== undefined) {
      setGeoPlaceId(draft.geoPlaceId ?? null, draft.geoPlaceName ?? null);
    }
    // Extended filters (v2)
    if (draft.sizes) setSizes(draft.sizes);
    if (draft.colors) setColors(draft.colors);
    if (draft.healthSpecialNeeds !== undefined) setHealthSpecialNeeds(draft.healthSpecialNeeds);
    
    // Track filter application for analytics
    const newFiltersCount = [
      draft.speciesKey ?? filters.speciesKey,
      (draft.breedIds ?? filters.breedIds).length > 0,
      (draft.sex ?? filters.sex).length > 0,
      (draft.ageBuckets ?? filters.ageBuckets).length > 0,
      draft.geoPlaceId ?? filters.geoPlaceId,
      (draft.sizes ?? filters.sizes).length > 0,
      (draft.colors ?? filters.colors).length > 0,
      (draft.healthSpecialNeeds ?? filters.healthSpecialNeeds) !== null,
    ].filter(Boolean).length;
    
    analyticsService.trackCatalogFilterApply(SCREEN_ID, {
      species: draft.speciesKey ?? filters.speciesKey,
      breed_ids: draft.breedIds ?? filters.breedIds,
      breed_count: (draft.breedIds ?? filters.breedIds).length,
      sex: draft.sex ?? filters.sex,
      age_buckets: draft.ageBuckets ?? filters.ageBuckets,
      geo_place_id: draft.geoPlaceId ?? filters.geoPlaceId,
      has_geo: !!(draft.geoPlaceId ?? filters.geoPlaceId),
      sizes: draft.sizes ?? filters.sizes,
      colors: draft.colors ?? filters.colors,
      health_special_needs: draft.healthSpecialNeeds ?? filters.healthSpecialNeeds,
      filter_source: 'modal',
      active_filters_count: newFiltersCount,
    });
    
    setIsFiltersModalOpen(false);
  };

  // Pull-to-refresh hook - only for mobile
  const { containerRef, pullDistance, isRefreshing } = usePullToRefresh({
    onRefresh: handleRefresh,
  });

  return (
    <div
      data-testid="tab-001-screen"
      className="h-[100dvh] w-full overflow-hidden flex justify-center bg-bg-accent-pink-page"
    >
      {/* Winter snow effect - catalog only */}
      <Snowfall />
      {/* Mobile container */}
      <div className="relative w-full max-w-[480px] h-[100dvh] overflow-hidden flex flex-col bg-bg-accent-pink-page">
        {/* Fixed header */}
        <div 
          className="fixed top-0 left-0 right-0 z-40 flex justify-center bg-bg-accent-pink-page"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="w-full max-w-[480px] bg-bg-accent-pink-page">
            <CatalogHeader
              avatarUrl={normalizeAssetUrl(viewer?.avatar_url) ?? undefined}
              hasNotification={viewer?.has_application_updates ?? false}
              onAnnouncementsPress={() => safeNavigate('/my-ads')}
              showAnnouncements={isCurator || isModerator}
              showModeration={isModerator}
              showAdmin={isAdmin}
            />
          </div>
        </div>

        {/* Scrollable content area */}
        <div 
          ref={isMobile ? containerRef : undefined}
          className="flex-1 overflow-y-auto scrollbar-hide"
          style={{
            paddingTop: `calc(env(safe-area-inset-top) + ${HEADER_HEIGHT}px)`,
            paddingBottom: `calc(env(safe-area-inset-bottom) + ${TAB_BAR_HEIGHT}px)`,
          }}
        >
          {/* Pull-to-refresh indicator - mobile only */}
          {isMobile && (
            <PullToRefreshIndicator
              pullDistance={pullDistance}
              isRefreshing={isRefreshing}
            />
          )}

          <div className="px-4">
            {/* Promo banners */}
            <div className="pt-3 pb-4">
              <PromoBanners />
            </div>

            {/* Search bar with city selector */}
            <div className="pb-3">
              <SearchBar
                value={searchQuery}
                onChange={handleSearchChange}
                debounceMs={500}
                selectedCityId={selectedCityId}
                selectedCityName={selectedCityName}
                onCitySelect={handleCitySelect}
                onSaveFilters={handleSaveFilters}
                canSaveFilters={!!user && activeFiltersCount > 0}
                isSavingFilters={isSavingFilters}
              />
            </div>

            <div className="pb-4">
              <FilterChips
                filters={filters}
                onSpeciesChange={setSpeciesKey}
                onAgeBucketToggle={toggleAgeBucket}
                onSortChange={setSort}
                onOpenFiltersModal={handleOpenFiltersModal}
                activeCount={activeFiltersCount}
                onBreedToggle={toggleBreedWithName}
                onBreedRemove={removeBreed}
                selectedBreeds={filters.breedIds.map(id => ({
                  id,
                  name: filters.breedNames[id] || 'Порода',
                }))}
                onResetFilters={() => {
                  resetFilters();
                  setIsAIFilterActive(false); // Reset AI filter when clearing all
                  analyticsService.trackCatalogFilterApply(SCREEN_ID, {
                    species: null,
                    breed_ids: [],
                    breed_count: 0,
                    sex: [],
                    age_buckets: [],
                    geo_place_id: null,
                    has_geo: false,
                    sizes: [],
                    colors: [],
                    health_special_needs: null,
                    filter_source: 'reset',
                    active_filters_count: 0,
                  });
                }}
                aiProfile={aiProfile}
                onApplyAIProfile={handleApplyAIProfile}
                isAIFilterActive={isAIFilterActive}
                onAIFilterActiveChange={setIsAIFilterActive}
              />
            </div>

            <CatalogList
              items={catalogItems}
              isLoading={isLoading}
              isLoadingMore={isLoadingMore}
              error={error}
              hasMore={catalogHasMore}
              onRetry={handleRetry}
              onLoadMore={handleLoadMore}
            />
          </div>
        </div>
      </div>

      <TabBar activeTab="friends" chatBadge={unreadCount} />

      {/* Catalog Filters Modal */}
      <CatalogFiltersModal
        isOpen={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        initialFilters={filters}
        onApply={handleApplyFilters}
      />

      {/* Save Filter Modal */}
      <SaveFilterModal
        open={isSaveModalOpen}
        onOpenChange={setIsSaveModalOpen}
        filterName={generateFilterDisplayName()}
        onSave={handleConfirmSaveFilters}
        isSaving={isSavingFilters}
      />
    </div>
  );
};

export default MainCatalog;
